import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import css from './charts.module.css';

interface Chart {
  data: any;
}

interface CustomTooltip {
  active?: any;
  payload?: any;
  label?: any;
}

export const CpuChart:FC<Chart> = ({ data }) => { 
    const { t } = useTranslation();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart 
          data={data} 
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0f51d4" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#0f51d4" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
          <XAxis 
            dataKey="logged_at" 
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
            }}
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <YAxis 
            dataKey="load" 
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
            label={{ value: '%', angle: -90, position: 'insideLeft', fill: '#666' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="load" 
            stroke="#0f51d4" 
            strokeWidth={2}
            fill="url(#colorCpu)" 
          />
        </AreaChart>
      </ResponsiveContainer>
    )
}

export const DiskChart:FC<Chart> = ({ data }) => { 
    const { t } = useTranslation();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart 
          data={data} 
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6f8fff" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#6f8fff" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
          <XAxis 
            dataKey="logged_at" 
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
            }}
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <YAxis 
            dataKey="write" 
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="write" 
            stroke="#6f8fff" 
            strokeWidth={2}
            fill="url(#colorDisk)" 
          />
        </AreaChart>
      </ResponsiveContainer>
    )
}

export const RamChart:FC<Chart> = ({ data }) => { 
    const { t } = useTranslation();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart 
          data={data} 
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorRamUsed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0f51d4" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#0f51d4" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorRamCached" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6edd67" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#6edd67" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
          <XAxis 
            dataKey="logged_at" 
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
            }}
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <YAxis 
            dataKey="used" 
            stroke="#666"
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="used_cached" 
            stroke="#6edd67" 
            strokeWidth={2}
            fill="url(#colorRamCached)" 
            stackId="1"
          />
          <Area 
            type="monotone" 
            dataKey="used" 
            stroke="#0f51d4" 
            strokeWidth={2}
            fill="url(#colorRamUsed)" 
            stackId="1"
          />
        </AreaChart>
      </ResponsiveContainer>
    )
}

export const CustomTooltip:FC<CustomTooltip> = ({ active, payload }) => { 
    function convertToDate(isoDate: any) {
        const date = new Date(isoDate);
        const options: any = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
        return new Intl.DateTimeFormat('ru-RU', options).format(date);
    }
    
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={css.tooltip}>
          <div className={css.tooltipHeader}>
            {convertToDate(data.logged_at)}
          </div>
          {data.used != undefined ?
          <>
            <div className={css.tooltipItem}>
              <div className={`${css.tooltipIndicator} ${css.tooltipIndicatorRamCached}`}></div>
              <span className={css.tooltipText}>
                Кэш: <strong className={css.tooltipValue}>{Math.round(data.used_cached)} MB</strong>
              </span>
            </div>
            <div className={css.tooltipItem}>
              <div className={`${css.tooltipIndicator} ${css.tooltipIndicatorRamUsed}`}></div>
              <span className={css.tooltipText}>
                Использовано: <strong className={css.tooltipValue}>{Math.round(data.used)} MB</strong>
              </span>
            </div>
          </>
            :
            <div className={css.tooltipItemSingle}>
              <div className={`${css.tooltipIndicator} ${data.write != undefined ? css.tooltipIndicatorDisk : css.tooltipIndicatorCpu}`}></div>
              <span className={css.tooltipText}>
                {data.write != undefined ? 'Запись' : 'Загрузка'}: <strong className={css.tooltipValue}>
                  {data.write != undefined ? Math.round(data.write) : Math.round(data.load)} {data.write != undefined ? 'req/s' : '%'}
                </strong>
              </span>
            </div>
          }
        </div>
      );
    }
  
    return null;
};
